import React from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import {
  Dashboard,
  ArcheIconManager,
  ArcheManagement,
  BetaKeyManager,
  MaintenanceControl,
  AdminReviewQueue,
} from './dev-console';
import { PaymentsDashboard } from './dev-console/pages/payments';
import { AnalyticsDashboard } from './dev-console/pages/analytics';
import {
  HomePage,
  BrowsePage,
  ArcheDetailPage,
  EntityDetailPage,
  CategoryPage,
  RunArchePage,
} from './marketplace';
import { ContributorDashboard, PluginSubmissions, PluginSubmissionDetail } from './contributor';
import { CreditsPage, CreditExhaustionListener } from './credits';
import { ThemeProvider } from '@/components/theme-provider';
import { Layout } from '@/components/Layout';
import { Toaster } from '@/components/ui/toaster';
import { ScrollToTop } from '@/components/ScrollToTop';
import { RouteTracker } from '@/components/RouteTracker';
import {
  AuthProvider,
  ProtectedRoute,
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  AccountSettingsPage,
  MyDataPage,
  SwitchDevUserPage,
  OAuthConsentPage,
  SessionExpiredListener,
  useAuth,
} from './auth';
import {
  ArcheInteractionPage,
  ConditionalProtectedRoute,
  PluginDevPreviewPage,
  ChatPage,
  BookingPage,
  BookingCancelPage,
  PublicListPage,
} from './arche';
import { CliAuthPage } from './developer/CliAuthPage';
import { MaintenancePage } from './components/MaintenancePage';
import { useMaintenanceMode } from './hooks/useMaintenanceMode';
import { SharedConversationPage } from './shared';
import { TermsOfServicePage, PrivacyPolicyPage, AcceptableUsePolicyPage } from './legal';

function Home() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="text-center">
          <div className="animate-pulse">
            <div className="h-16 w-16 bg-muted rounded mx-auto mb-4" />
            <div className="h-8 bg-muted rounded w-48 mx-auto mb-4" />
            <div className="h-4 bg-muted rounded w-64 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  // Redirect authenticated users to the main arche interface
  if (isAuthenticated) {
    return <Navigate to="/a/arche_make_a_request" replace />;
  }

  return (
    <div className="flex-1 flex items-center justify-center px-4 pb-20">
      <div className="w-full max-w-sm">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <img src="/fias-logo.png" alt="Fias" className="h-16 w-auto mx-auto mb-4 dark:invert" />
          <h1 className="text-2xl font-semibold text-foreground">Welcome to Fias</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sign in to explore AI-powered applications
          </p>
        </div>

        {/* Card */}
        <div className="bg-card rounded-xl border shadow-sm p-6">
          <div className="space-y-4">
            <Link to="/auth/login" className="block">
              <button className="w-full h-11 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors">
                Sign in
              </button>
            </Link>
            <Link to="/auth/signup" className="block">
              <button className="w-full h-11 bg-secondary text-secondary-foreground rounded-md font-medium hover:bg-secondary/90 transition-colors">
                Create account
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { isActive, message } = useMaintenanceMode();
  const { user } = useAuth();

  if (isActive && !user?.roles?.includes('admin')) {
    return <MaintenancePage message={message} />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="fias-theme" attribute="class">
      <AuthProvider>
        <Toaster />
        <ScrollToTop />
        {/* Fires `page_view` analytics events on every navigation.
            Mounted inside <BrowserRouter /> (set in main.tsx) so it can
            use useLocation. Renders nothing. */}
        <RouteTracker />
        {/* Listens for `fias:credits-exhausted` events from the API/streaming
            interceptors and navigates the user to the credits store via
            react-router. Mounted inside the router context so it can use
            useNavigate. Renders nothing. */}
        <CreditExhaustionListener />
        {/* Listens for `fias:session-expired` events from the api.ts 401
            interceptor and the AuthContext proactive refresh timer.
            Shows a modal with a Sign In button that preserves the user's
            current location for return after re-authentication. */}
        <SessionExpiredListener />
        <MaintenanceGate>
          <Routes>
            {/* Auth routes (public, no header) */}
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/signup" element={<SignupPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />

            {/* Legal pages (public, no auth required) */}
            <Route path="/legal/terms" element={<TermsOfServicePage />} />
            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/acceptable-use" element={<AcceptableUsePolicyPage />} />

            {/* Shared conversation (public, no auth required) */}
            <Route path="/shared/:shareId" element={<SharedConversationPage />} />

            {/* All other routes with Layout (header) */}
            <Route
              path="/"
              element={
                <Layout>
                  <Home />
                </Layout>
              }
            />
            <Route
              path="/auth/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AccountSettingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/auth/switch-user"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SwitchDevUserPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-data"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MyDataPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* OAuth consent screen — auth required, with layout */}
            <Route
              path="/oauth/authorize"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OAuthConsentPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* CLI auth — creates API key and redirects to localhost callback */}
            <Route
              path="/developer/cli-auth"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CliAuthPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Plugin dev preview — local-development-only.
                The page loads arbitrary plugin code into an iframe and
                hands it a real platform bridge + auth token. In any
                non-development build a phishing link of the shape
                /a/arche_plugins/dev-preview?url=https://attacker
                would otherwise drain credits / read storage as the
                victim. We omit the route from the build entirely
                outside `pnpm dev`; even an authenticated victim hitting
                the URL in production gets a 404 from the catch-all. */}
            {import.meta.env.DEV && (
              <Route
                path="/a/arche_plugins/dev-preview"
                element={
                  <ProtectedRoute>
                    <PluginDevPreviewPage />
                  </ProtectedRoute>
                }
              />
            )}
            {/* Community chat lives at /chat */}
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChatPage defaultChannelId="cch_global_fias_community" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Calendar booking pages — no auth, no layout */}
            <Route path="/a/arche_calendar/book/:slug" element={<BookingPage />} />
            <Route path="/a/arche_calendar/cancel/:slug" element={<BookingCancelPage />} />
            {/* Public todo list pages — no auth, no layout */}
            <Route path="/a/arche_todo_lists/public/:slug" element={<PublicListPage />} />
            <Route path="/a/arche_todo_lists/public/:slug/completed" element={<PublicListPage />} />
            {/* Arche interaction route (conditionally protected - public arches don't require auth) */}
            <Route
              path="/a/:id/*"
              element={
                <ConditionalProtectedRoute>
                  <ArcheInteractionPage />
                </ConditionalProtectedRoute>
              }
            />
            {/* Marketplace routes (protected) */}
            <Route
              path="/marketplace"
              element={
                <ProtectedRoute>
                  <Layout>
                    <HomePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/browse"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BrowsePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/arches/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ArcheDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/entities/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EntityDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/arches/:id/run"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RunArchePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/categories/:slug"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CategoryPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Contributor routes (protected) */}
            <Route
              path="/contributor/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContributorDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/*
              Submission pages are for any authenticated contributor — they
              show *their own* submissions. The detail endpoint is the
              authorization boundary (a contributor can only fetch a submission
              they own; admins can fetch any). Admin actions inside the detail
              page are gated client-side by isAdmin AND backend-gated.
            */}
            <Route
              path="/submissions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PluginSubmissions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/submissions/:submissionId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PluginSubmissionDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Credits routes (protected) */}
            <Route
              path="/credits"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreditsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Dev console routes (protected, admin only) */}
            <Route
              path="/dev-console"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-console/arche-icons"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <ArcheIconManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-console/arche-management"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <ArcheManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-console/beta-keys"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <BetaKeyManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-console/payments"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <PaymentsDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-console/analytics"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <AnalyticsDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-console/maintenance"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <MaintenanceControl />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-console/admin/reviews"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Layout>
                    <AdminReviewQueue />
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </MaintenanceGate>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
